import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { SidebarEventTypes } from '~/src/dash/analytics/analytics';
import { BetaTooltipIcon } from '~/src/dash/components/icons/beta-icon';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import {
  Col,
  Cols,
  spacing,
  Text,
  TKey,
  usePanel,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { getOversizedLanguageUIFriendlyStyle } from '~/src/dash/publicaUI/common';
import { extractRouteParams, getRoutePath } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { IS_AUTHORIZED_OR } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { useNavBarStore } from '~/src/dash/views/navbar/contexts';
import { missingPermToText, trackEvent } from '~/src/dash/views/navbar/NavBar/common/consts';
import { IRoute } from '~/src/dash/views/navbar/NavBar/common/types';

interface INavBarSubRowProps {
  label: string;
  route: IRoute;
  isDisabled: boolean;
  permChecks?: string[];
  pathString: string;
  eventName: SidebarEventTypes;
  customActiveCheck?: (pathname: string) => boolean;
  isBeta?: boolean;
  tKey?: TKey;
  uiFriendlyTKey?: TKey; // used if a string needs to be modified to better fit the ui
}

export const NavBarSubRow = ({
  label,
  route,
  isDisabled,
  permChecks,
  pathString,
  eventName,
  customActiveCheck,
  isBeta = false,
  tKey,
  uiFriendlyTKey,
}: INavBarSubRowProps) => {
  const { theme } = usePanel();
  const { session, user, location } = useBaseStore();
  const { i18n } = usePublicaTranslation();
  const { isDarkTheme } = useNavBarStore();
  const [isHover, setIsHover] = useState(false);
  const path = getRoutePath(
    route,
    extractRouteParams({
      session,
      user,
    }),
  );
  const isActive = customActiveCheck
    ? customActiveCheck(location.pathname)
    : location.pathname.indexOf(pathString) > -1;

  // check if this specific sub row is disabled
  const isItemDisabled =
    permChecks && permChecks.length > 0
      ? missingPermToText(IS_AUTHORIZED_OR(permChecks, user.user)) !== undefined
      : false;
  // save in case we want to add a tooltip with missing permissionson specific items
  // const tooltipText: React.ReactNode = permChecks && permChecks.length > 0
  //   ? missingPermToText(IS_AUTHORIZED_OR(permChecks, user.user))
  //   : <div>{label}</div>;
  const isSubRowDisabled = isItemDisabled || isDisabled;

  const handleLinkClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isSubRowDisabled) {
      e.preventDefault();
    } else {
      trackEvent(eventName, user);
    }
  };

  return (
    <NavLink
      onClick={handleLinkClick}
      to={path}
      style={{
        textDecoration: 'none',
      }}
    >
      <button
        style={{
          padding: 0,
          margin: 0,
          width: '100%',
          border: 'none',
          backgroundColor:
            isHover && !isSubRowDisabled
              ? theme.menuBarHovered
              : isDarkTheme
              ? publicaColors.veryDarkGray
              : theme.menuBarSelected,
          fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        }}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <Cols
          style={{
            justifyContent: 'flex-start',
            alignItems: 'center',
            cursor: isSubRowDisabled ? 'default' : 'pointer',
          }}
        >
          <Col
            width={24}
            style={{
              height: '12px',
              borderRight: isActive ? `2px solid ${theme.menuBarHovered}` : 'none',
            }}
          ></Col>
          <Col smallPadding paddingLeft={spacing.medium} style={{ maxWidth: '168px' }}>
            <Text
              color={isHover && isDarkTheme ? '#000' : theme.menuBarText}
              style={{
                opacity: isActive || (isHover && !isSubRowDisabled) ? 1 : 0.7,
                fontWeight: isActive ? FONT_WEIGHT.bold : FONT_WEIGHT.base,
                textAlign: 'left',
                verticalAlign: 'middle',
                ...getOversizedLanguageUIFriendlyStyle(i18n.language),
              }}
              tKey={uiFriendlyTKey || tKey}
            >
              {label}
            </Text>
          </Col>
          {isBeta && (
            <Col displayFlex>
              <BetaTooltipIcon />
            </Col>
          )}
        </Cols>
      </button>
    </NavLink>
  );
};
